import React, { useRef, useState } from 'react';
import { Button, Card, Modal, Popconfirm, Space, Tag, Typography, message } from 'antd';
import { EditOutlined, UploadOutlined, PlayCircleOutlined, EyeOutlined } from '@ant-design/icons';
import AiDraftBadge from '../AiDraftBadge';
import { useApiRequest } from '../../hooks/useApiRequest';
import { openServerUrl, preopenWindow, userMessageForError } from '../../utils/apiError';
import { reportOpenFailure } from '../../utils/openLinkFeedback';
import {
    MODULE_TYPE_LABELS,
    TRANSLATION_STATUS_LABELS,
    languageLabel,
    moduleNeedsReview,
    moduleApprovePath,
    formatDdMmYyyy,
    TRANSLATIONS_LOCKED_MESSAGE,
} from '../../utils/trainingModel';

const { Text, Paragraph } = Typography;

const STATUS_COLOURS = { pending_review: 'orange', approved: 'green', rejected: 'red' };

function ContentBody({ moduleType, content }) {
    if (!content) return <Text type="secondary">No content yet.</Text>;
    if (moduleType === 'reading') {
        return <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{content.text}</Paragraph>;
    }
    if (moduleType === 'quiz' && Array.isArray(content.questions)) {
        return (
            <ol style={{ paddingLeft: 18, margin: 0 }}>
                {content.questions.map((q, i) => (
                    <li key={i} style={{ marginBottom: 8 }}>
                        <Text strong>{q.prompt}</Text>
                        <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '4px 0 0' }}>
                            {q.options.map((o, oi) => (
                                <li key={oi}>
                                    {oi === q.correctIndex ? <Tag color="green">Answer</Tag> : null}
                                    {o}
                                </li>
                            ))}
                        </ul>
                    </li>
                ))}
            </ol>
        );
    }
    return <Text type="secondary">Nothing to preview.</Text>;
}

/**
 * One module of a course, for the authoring surface (the parent only renders this for
 * Advisor / Lead Advisor / platform admin). Covers: AI-draft chip + confidence, review/edit,
 * video upload + preview (SAS-signed URL), per-language translation review.
 *
 * `canEditContent` is true only while the course is a draft — a published course is changed
 * by forking a new version (Commandment VII), so edit/upload controls are not offered then.
 * Translation review is NOT available on a published course (the API returns 409; see
 * TRANSLATIONS_LOCKED_MESSAGE): translations are reviewed on the draft, then published with it.
 */
const ModuleCard = ({ module, projectId, canEditContent, onEdit, onChanged }) => {
    const { request } = useApiRequest();
    const fileRef = useRef(null);
    const [showContent, setShowContent] = useState(false);
    const [previewTranslation, setPreviewTranslation] = useState(null);
    const [busy, setBusy] = useState(null);

    const needsReview = moduleNeedsReview(module);
    const isTextModule = module.moduleType === 'reading' || module.moduleType === 'quiz';

    const handleVideoFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setBusy('upload');
        try {
            const formData = new FormData();
            formData.append('file', file);
            await request('POST', `/projects/${projectId}/training/modules/${module.id}/video`, { formData });
            message.success('Video uploaded.');
            onChanged();
        } catch (err) {
            if (err.status === 415) message.error('That file is not a genuine MP4, WEBM or MOV video. The file contents are checked, not just its name.');
            else if (err.status === 413) message.error('That video is too large to upload. Compress it or upload a shorter file.');
            else message.error(err.message || 'Video upload failed.');
        } finally {
            setBusy(null);
        }
    };

    const handlePreviewVideo = async () => {
        setBusy('preview');
        const win = preopenWindow();
        try {
            const { data } = await request('GET', `/projects/${projectId}/training/modules/${module.id}/video`);
            if (!openServerUrl(data?.url, win)) reportOpenFailure(message, data?.url);
        } catch (err) {
            if (win && !win.closed) win.close();
            message.error(userMessageForError(err, 'Could not open the video.'));
        } finally {
            setBusy(null);
        }
    };

    const approveModule = async () => {
        setBusy('approve');
        try {
            await request('POST', moduleApprovePath(projectId, module.id));
            message.success('Module approved.');
            onChanged();
        } catch (err) {
            message.error(err.message || 'Could not approve the module.');
        } finally {
            setBusy(null);
        }
    };

    const reviewTranslation = async (language, decision) => {
        setBusy(`${language}:${decision}`);
        try {
            await request('POST', `/projects/${projectId}/training/modules/${module.id}/translations/${language}/review`, {
                body: { decision },
            });
            message.success(`${languageLabel(language)} translation ${decision}.`);
            onChanged();
        } catch (err) {
            message.error(err.message || 'Could not record the review.');
        } finally {
            setBusy(null);
        }
    };

    return (
        <Card size="small" data-testid={`module-card-${module.id}`} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <Space wrap>
                        <Text type="secondary">#{module.ordinal}</Text>
                        <Tag>{MODULE_TYPE_LABELS[module.moduleType] || module.moduleType}</Tag>
                        <Text strong>{module.title}</Text>
                        {typeof module.durationMinutes === 'number' && <Text type="secondary">{module.durationMinutes} min</Text>}
                    </Space>
                    <div style={{ marginTop: 6 }}>
                        <AiDraftBadge
                            sourceType={module.sourceType}
                            confidence={module.confidence}
                            reviewed={!needsReview}
                            reviewedAt={module.reviewedAt}
                        />
                        {module.sourceType !== 'ai_generated' && module.reviewedAt && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Reviewed {formatDdMmYyyy(module.reviewedAt)}
                            </Text>
                        )}
                    </div>
                </div>
                <Space wrap>
                    {isTextModule && (
                        <Button size="small" icon={<EyeOutlined />} onClick={() => setShowContent((v) => !v)}>
                            {showContent ? 'Hide content' : 'Show content'}
                        </Button>
                    )}
                    {module.moduleType === 'video' && module.contentBlobUrl && (
                        <Button size="small" icon={<PlayCircleOutlined />} loading={busy === 'preview'} onClick={handlePreviewVideo}>
                            Preview video
                        </Button>
                    )}
                    {canEditContent && module.moduleType === 'video' && (
                        <>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                                style={{ display: 'none' }}
                                aria-label={`Upload video for ${module.title}`}
                                onChange={handleVideoFile}
                            />
                            <Button size="small" icon={<UploadOutlined />} loading={busy === 'upload'} onClick={() => fileRef.current && fileRef.current.click()}>
                                {module.contentBlobUrl ? 'Replace video' : 'Upload video'}
                            </Button>
                        </>
                    )}
                    {canEditContent && (
                        <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(module)}>Edit</Button>
                    )}
                    {needsReview && canEditContent && (
                        <Popconfirm
                            title="Approve this AI-drafted module?"
                            description="Confirm you have read it and corrected anything wrong. Your approval is recorded, and this course cannot be published until every AI-drafted module is approved."
                            okText="Approve"
                            onConfirm={approveModule}
                        >
                            <Button size="small" type="primary" loading={busy === 'approve'}>Review &amp; approve</Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            {module.moduleType === 'live_session' && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Held in a scheduled project meeting - learners join from Meetings.
                </Text>
            )}
            {module.moduleType === 'video' && !module.contentBlobUrl && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    No video uploaded yet. Learners cannot watch this module until one is.
                </Text>
            )}

            {showContent && isTextModule && (
                <div style={{ marginTop: 10, padding: 10, background: 'var(--color-surface-sunken)', borderRadius: 8 }}>
                    <ContentBody moduleType={module.moduleType} content={module.contentJson} />
                </div>
            )}

            {isTextModule && (module.translations || []).length > 0 && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Translations (approved translations are served to learners who choose that language, while the source text is unchanged)</Text>
                    {!canEditContent && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{TRANSLATIONS_LOCKED_MESSAGE}</Text>}
                    {module.translations.map((t) => (
                        <div
                            key={t.language}
                            data-testid={`translation-${module.id}-${t.language}`}
                            style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginTop: 6 }}
                        >
                            <Space wrap>
                                <Text strong>{languageLabel(t.language)}</Text>
                                <Tag color={STATUS_COLOURS[t.reviewStatus] || 'default'}>
                                    {TRANSLATION_STATUS_LABELS[t.reviewStatus] || t.reviewStatus}
                                </Tag>
                                {t.stale && <Tag color="red">Out of date - re-translate</Tag>}
                                <AiDraftBadge
                                    sourceType={t.sourceType}
                                    confidence={t.confidence}
                                    reviewed={t.reviewStatus === 'approved'}
                                    reviewedAt={t.reviewedAt}
                                />
                            </Space>
                            <Space wrap>
                                <Button size="small" onClick={() => setPreviewTranslation(t)}>View text</Button>
                                {canEditContent && t.reviewStatus !== 'approved' && !t.stale && (
                                    <Popconfirm
                                        title={`Approve the ${languageLabel(t.language)} translation?`}
                                        description="Confirm you have read it. Once every module in this language is approved, learners can choose it; editing the source text later resets it to pending review."
                                        okText="Approve"
                                        onConfirm={() => reviewTranslation(t.language, 'approved')}
                                    >
                                        <Button size="small" type="primary" loading={busy === `${t.language}:approved`}>Approve</Button>
                                    </Popconfirm>
                                )}
                                {canEditContent && t.reviewStatus !== 'rejected' && (
                                    <Button size="small" danger loading={busy === `${t.language}:rejected`} onClick={() => reviewTranslation(t.language, 'rejected')}>
                                        {t.reviewStatus === 'approved' ? 'Revoke' : 'Reject'}
                                    </Button>
                                )}
                            </Space>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                open={!!previewTranslation}
                title={previewTranslation ? `${module.title} - ${languageLabel(previewTranslation.language)}` : ''}
                footer={null}
                onCancel={() => setPreviewTranslation(null)}
                width={680}
            >
                {previewTranslation && <ContentBody moduleType={module.moduleType} content={previewTranslation.contentJson} />}
            </Modal>
        </Card>
    );
};

export default ModuleCard;
